<template>
  <main class="view statistics">
    <div class="descriptor">
      <div class="descriptor-header">
        <div>
          <h1>{{ $t('statistics.headline') }}</h1>
          <p>{{ $t('statistics.copy') }}</p>
        </div>
        <div class="descriptor-actions">
          <Button primary :disabled="loading || exporting" @click="exportCsv">
            {{ exporting ? 'CSV wird erstellt...' : $t('statistics.buttons.exportCsv') }}
          </Button>
        </div>
      </div>

      <div class="filter-panel">
        <div class="filter-row presets">
          <span class="filter-label">Schuljahr (01.08. – 31.07.):</span>
          <div class="preset-pills">
            <button
              type="button"
              class="pill"
              :class="{ active: activePreset === 'all' }"
              @click="selectPreset('all', null, null)"
            >
              Alle Zeiträume
            </button>
            <button
              v-for="sy in schoolYearPresets"
              :key="sy.id"
              type="button"
              class="pill"
              :class="{ active: activePreset === sy.id }"
              @click="selectPreset(sy.id, sy.start, sy.end)"
            >
              SJ {{ sy.id }}
            </button>
          </div>
        </div>

        <div class="filter-row controls">
          <div class="control-group">
            <label for="stat-date-from">Archiviert von</label>
            <input
              id="stat-date-from"
              type="date"
              v-model="filters.from"
              @change="onCustomDateChange"
            />
          </div>
          <div class="control-group">
            <label for="stat-date-to">Archiviert bis</label>
            <input
              id="stat-date-to"
              type="date"
              v-model="filters.to"
              @change="onCustomDateChange"
            />
          </div>
          <div class="control-group branch-select">
            <label for="stat-branch">Abklärungsstelle (SPD)</label>
            <select id="stat-branch" v-model="filters.branch" @change="loadStatistics">
              <option value="all">Ganzer Kanton (Alle SPD)</option>
              <option
                v-for="b in availableBranches"
                :key="b.uuid"
                :value="b.uuid"
              >
                {{ b.name }}
              </option>
            </select>
          </div>
          <div class="control-actions">
            <Button primary small :disabled="loading" @click="loadStatistics">
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-box">
      <Loader />
      <span>Statistiken werden berechnet...</span>
    </div>

    <template v-else-if="stats">
      <!-- KPI Summary Cards -->
      <section class="kpi-grid">
        <div class="kpi-card primary">
          <span class="kpi-label">Archivierte SAV (Auswahl)</span>
          <span class="kpi-value">{{ stats.meta.totalInquiries }}</span>
          <span class="kpi-sub">
            {{ stats.meta.uniqueSavIds }} eindeutige SAV-IDs
          </span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">SAV Version 2.0</span>
          <span class="kpi-value">{{ stats.meta.v2Count }}</span>
          <span class="kpi-sub">
            {{ formatPct(stats.meta.v2Count, stats.meta.totalInquiries) }}% der Auswahl
          </span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">SAV Version 1.0</span>
          <span class="kpi-value">{{ stats.meta.v1Count }}</span>
          <span class="kpi-sub">
            davon {{ stats.meta.legacyCount }} Altdaten-Import (bis 2022)
          </span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">Aktiver Zeitraum (Archivierungsdatum)</span>
          <span class="kpi-value period">{{ formattedPeriod }}</span>
          <span class="kpi-sub">
            {{ selectedBranchLabel }}
          </span>
        </div>
      </section>

      <!-- School-Year Overview & SPD Breakdown -->
      <section class="overview-panels">
        <div class="panel-card">
          <div class="panel-header">
            <div>
              <h2>Vollständig ausgefüllte &amp; archivierte SAV nach Schuljahr (21/22 – 25/26)</h2>
              <p class="panel-sub">
                Stichtag: Archivierungsdatum (01.08. bis 31.07. des Folgejahres) — Klicken Sie auf ein Schuljahr zum Filtern.
              </p>
            </div>
          </div>
          <table class="stat-table interactive">
            <thead>
              <tr>
                <th>Schuljahr</th>
                <th>Zeitraum</th>
                <th class="num">Archivierte SAV</th>
                <th class="num">Eindeutige SAV-IDs</th>
                <th class="num">SAV v1</th>
                <th class="num">SAV v2</th>
                <th class="bar-col">Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sy in stats.schoolYears"
                :key="sy.id"
                :class="{ selected: activePreset === sy.id }"
                @click="selectPreset(sy.id, sy.start, sy.end)"
              >
                <td class="bold">Schuljahr {{ sy.id }}</td>
                <td class="muted">{{ formatDateCH(sy.start) }} – {{ formatDateCH(sy.end) }}</td>
                <td class="num bold">{{ sy.total }}</td>
                <td class="num">{{ sy.uniqueSavId }}</td>
                <td class="num">{{ sy.v1Count }}</td>
                <td class="num">{{ sy.v2Count }}</td>
                <td class="bar-col">
                  <div class="mini-bar-wrap">
                    <div
                      class="mini-bar"
                      :style="{ width: `${formatPct(sy.total, maxSchoolYearTotal)}%` }"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <details class="panel-card collapsible">
          <summary class="panel-header">
            <div>
              <h2>Verteilung nach Abklärungsstelle / SPD ({{ stats.meta.totalInquiries }} SAV)</h2>
              <p class="panel-sub">Anzahl archivierter SAV pro Dienst im gewählten Zeitraum</p>
            </div>
          </summary>
          <div class="branch-bars">
            <div
              v-for="b in nonEmptyBranches"
              :key="b.uuid"
              class="bar-row"
            >
              <div class="bar-label">
                <span class="title">{{ b.name }}</span>
                <span class="meta" v-if="b.district">({{ b.district }})</span>
              </div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: `${b.percentage}%` }" />
              </div>
              <div class="bar-stats">
                <span class="count">{{ b.count }}</span>
                <span class="pct">({{ b.percentage.toFixed(1) }}%)</span>
              </div>
            </div>
          </div>
        </details>
      </section>

      <!-- Section Filter Tabs -->
      <section class="section-nav">
        <button
          type="button"
          class="sec-tab"
          :class="{ active: activeSection === 'all' }"
          @click="activeSection = 'all'"
        >
          Alle 11 Sektionen
        </button>
        <button
          v-for="sec in stats.sections"
          :key="`tab-${sec.number}`"
          type="button"
          class="sec-tab"
          :class="{ active: activeSection === sec.number }"
          @click="activeSection = sec.number"
        >
          {{ sec.number }}. {{ sec.label }}
        </button>
      </section>

      <!-- Section-by-Section Field Visualizations -->
      <section
        v-for="sec in visibleSections"
        :key="`sec-${sec.number}`"
        class="stat-section"
      >
        <div class="section-banner">
          <h2>{{ sec.heading }}</h2>
        </div>

        <div class="fields-grid">
          <div
            v-for="field in sec.fields"
            :key="field.id"
            class="field-card"
            :class="{ 'full-width': field.type === 'icf-matrix' || field.type === 'goals-matrix' }"
          >
            <div class="field-header">
              <div>
                <h3>{{ field.label }}</h3>
                <span v-if="field.note" class="field-badge">{{ field.note }}</span>
              </div>
              <div class="field-n">
                <span class="answered">N = {{ field.totalAnswered }}</span>
                <span v-if="field.totalMissing > 0" class="missing">
                  (Keine Angabe / v1: {{ field.totalMissing }})
                </span>
              </div>
            </div>

            <!-- Standard Single / Multiple / Year Distribution Bars -->
            <template v-if="field.type === 'single' || field.type === 'multiple' || field.type === 'year-distribution'">
              <div v-if="field.items.length === 0" class="empty-hint">
                Keine Daten für diesen Zeitraum vorhanden.
              </div>
              <div v-else class="bar-list" :class="{ compact: field.type === 'year-distribution' }">
                <div
                  v-for="item in field.items"
                  :key="`${field.id}-${item.code}`"
                  class="bar-row"
                  :class="{ zero: item.count === 0 }"
                >
                  <div class="bar-label" :title="item.label">
                    {{ item.label }}
                  </div>
                  <div class="bar-track">
                    <div
                      class="bar-fill"
                      :style="{ width: `${Math.min(100, item.percentage)}%` }"
                    />
                  </div>
                  <div class="bar-stats">
                    <span class="count">{{ item.count }}</span>
                    <span class="pct">({{ item.percentage.toFixed(1) }}%)</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- Section 6 ICF Matrix Table -->
            <template v-else-if="field.type === 'icf-matrix'">
              <div class="table-scroll">
                <table class="stat-table matrix-table">
                  <thead>
                    <tr>
                      <th>ICF-Code</th>
                      <th>Bezeichnung</th>
                      <th class="num">N</th>
                      <th class="num c1">Kein Problem / Ressource</th>
                      <th class="num c2">Problem leicht-mässig</th>
                      <th class="num c3">Problem erheblich-voll</th>
                      <th class="num c4">K.A. / nicht anwendb.</th>
                      <th class="stack-col">Verteilung</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in field.rows" :key="`${field.id}-${row.code}`">
                      <td class="code-cell">{{ row.code }}</td>
                      <td>{{ row.label }}</td>
                      <td class="num muted">{{ row.totalAnswered }}</td>
                      <td class="num">
                        <strong>{{ row.counts[1] }}</strong>
                        <span class="sub-pct">({{ row.percentages[1].toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.counts[2] }}</strong>
                        <span class="sub-pct">({{ row.percentages[2].toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.counts[3] }}</strong>
                        <span class="sub-pct">({{ row.percentages[3].toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.counts[4] }}</strong>
                        <span class="sub-pct">({{ row.percentages[4].toFixed(1) }}%)</span>
                      </td>
                      <td class="stack-col">
                        <div class="stacked-bar">
                          <span
                            class="seg seg-1"
                            :style="{ width: `${row.percentages[1]}%` }"
                            :title="`Kein Problem: ${row.percentages[1]}%`"
                          />
                          <span
                            class="seg seg-2"
                            :style="{ width: `${row.percentages[2]}%` }"
                            :title="`Leicht-mässig: ${row.percentages[2]}%`"
                          />
                          <span
                            class="seg seg-3"
                            :style="{ width: `${row.percentages[3]}%` }"
                            :title="`Erheblich-voll: ${row.percentages[3]}%`"
                          />
                          <span
                            class="seg seg-4"
                            :style="{ width: `${row.percentages[4]}%` }"
                            :title="`K.A.: ${row.percentages[4]}%`"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- Section 8 Goals Matrix Table -->
            <template v-else-if="field.type === 'goals-matrix'">
              <div class="table-scroll">
                <table class="stat-table matrix-table">
                  <thead>
                    <tr>
                      <th>Entwicklungsbereich</th>
                      <th class="num">N</th>
                      <th class="num">Aktuell: Alters-/Lehrplangemäss</th>
                      <th class="num">Aktuell: Individualisiert</th>
                      <th class="num">Anvisiert (1–2 J.): Altersgemäss</th>
                      <th class="num">Anvisiert (1–2 J.): Individualisiert</th>
                      <th class="num">Schwerpunkt Förderung</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rIdx) in field.rows" :key="`goal-${rIdx}`">
                      <td class="bold">{{ row.label }}</td>
                      <td class="num muted">{{ row.totalAnswered }}</td>
                      <td class="num">
                        <strong>{{ row.aktuell.altersgemaessCount }}</strong>
                        <span class="sub-pct">({{ row.aktuell.altersgemaessPct.toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.aktuell.individualisiertCount }}</strong>
                        <span class="sub-pct">({{ row.aktuell.individualisiertPct.toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.anvisiert.altersgemaessCount }}</strong>
                        <span class="sub-pct">({{ row.anvisiert.altersgemaessPct.toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.anvisiert.individualisiertCount }}</strong>
                        <span class="sub-pct">({{ row.anvisiert.individualisiertPct.toFixed(1) }}%)</span>
                      </td>
                      <td class="num">
                        <strong>{{ row.schwerpunktCount }}</strong>
                        <span class="sub-pct">({{ row.schwerpunktPct.toFixed(1) }}%)</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </section>
    </template>
  </main>
</template>

<script>
  import Papa from 'papaparse';
  import Button from '@/components/ui/inputs/Button';
  import Loader from '@/components/ui/Loader';
  import Toast from '@/components/ui/Toast';
  import UserRoles from '@/objects/UserRoles';
  import { Store } from '@/store';

  export default {
    name: 'Statistics',
    components: {
      Button,
      Loader,
    },
    data() {
      return {
        loading: true,
        exporting: false,
        activePreset: '25/26',
        activeSection: 'all',
        filters: {
          from: '2025-08-01',
          to: '2026-07-31',
          branch: 'all',
        },
        availableBranches: [],
        stats: null,
        schoolYearPresets: [
          { id: '21/22', start: '2021-08-01', end: '2022-07-31' },
          { id: '22/23', start: '2022-08-01', end: '2023-07-31' },
          { id: '23/24', start: '2023-08-01', end: '2024-07-31' },
          { id: '24/25', start: '2024-08-01', end: '2025-07-31' },
          { id: '25/26', start: '2025-08-01', end: '2026-07-31' },
        ],
      };
    },
    computed: {
      visibleSections() {
        if (!this.stats || !this.stats.sections) {
          return [];
        }
        if (this.activeSection === 'all') {
          return this.stats.sections;
        }
        return this.stats.sections.filter(s => s.number === this.activeSection);
      },
      nonEmptyBranches() {
        if (!this.stats || !this.stats.branches) {
          return [];
        }
        return this.stats.branches;
      },
      maxSchoolYearTotal() {
        if (!this.stats || !this.stats.schoolYears) {
          return 1;
        }
        const max = Math.max(...this.stats.schoolYears.map(s => s.total), 1);
        return max;
      },
      formattedPeriod() {
        if (this.activePreset !== 'all' && this.activePreset !== 'custom') {
          return `SJ ${this.activePreset}`;
        }
        const { from, to } = this.filters;
        if (!from && !to) {
          return 'Alle Schuljahre';
        }
        return `${from ? this.formatDateCH(from) : 'Beginn'} – ${to ? this.formatDateCH(to) : 'Heute'}`;
      },
      selectedBranchLabel() {
        if (this.filters.branch === 'all') {
          return 'Ganzer Kanton Zürich';
        }
        const found = this.availableBranches.find(b => b.uuid === this.filters.branch);
        return found ? found.name : 'Ausgewählter SPD';
      },
    },
    async created() {
      if (Store.role !== UserRoles.VSA_MANAGER) {
        this.$router.push('/');
        return;
      }
      await this.loadStatistics();
    },
    methods: {
      async loadStatistics() {
        this.loading = true;
        const params = {};
        if (this.filters.from) {
          params.from = this.filters.from;
        }
        if (this.filters.to) {
          params.to = this.filters.to;
        }
        if (this.filters.branch && this.filters.branch !== 'all') {
          params.branch = this.filters.branch;
        }

        const response = await this.$sav.api.get('/statistics', params);
        if (response) {
          this.stats = response;
          if (this.availableBranches.length === 0 && response.branches) {
            this.availableBranches = response.branches.map(b => ({
              uuid: b.uuid,
              name: b.name,
            }));
          }
        }
        this.loading = false;
      },
      selectPreset(id, start, end) {
        this.activePreset = id;
        this.filters.from = start || '';
        this.filters.to = end || '';
        this.loadStatistics();
      },
      onCustomDateChange() {
        this.activePreset = (!this.filters.from && !this.filters.to) ? 'all' : 'custom';
      },
      async exportCsv() {
        this.exporting = true;
        try {
          const params = {};
          if (this.filters.from) {
            params.from = this.filters.from;
          }
          if (this.filters.to) {
            params.to = this.filters.to;
          }
          if (this.filters.branch && this.filters.branch !== 'all') {
            params.branch = this.filters.branch;
          }

          const rows = await this.$sav.api.get('/statistics/export', params);
          if (!rows || rows.length === 0) {
            Toast.warning('Keine archivierten SAV im gewählten Zeitraum für den Export gefunden.');
            this.exporting = false;
            return;
          }

          const csvContent = Papa.unparse(rows, {
            delimiter: ';',
            header: true,
          });
          const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const fromPart = this.filters.from || 'alle';
          const toPart = this.filters.to || 'heute';
          link.href = url;
          link.setAttribute('download', `sav-statistik-${fromPart}_bis_${toPart}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (err) {
          Toast.error('Fehler beim Erstellen des CSV-Exports.');
        }
        this.exporting = false;
      },
      formatPct(value, total) {
        if (!total || total <= 0) {
          return '0.0';
        }
        return ((value / total) * 100).toFixed(1);
      },
      formatDateCH(ymd) {
        if (!ymd || typeof ymd !== 'string') {
          return '';
        }
        const parts = ymd.split('-');
        if (parts.length === 3) {
          return `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
        return ymd;
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.statistics {
  .descriptor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    flex-wrap: wrap;
  }

  .filter-panel {
    margin-top: 24px;
    background-color: $color-white-solid-10;
    border: 1px solid $color-black-alpha-10;
    border-radius: $default-border-radius;
    padding: 20px 24px;
    box-shadow: $box-shadow-1;

    .filter-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;

      &.presets {
        padding-bottom: 16px;
        margin-bottom: 16px;
        border-bottom: 1px solid $color-black-alpha-05;
      }
    }

    .filter-label {
      @include font-body-medium-semibold;
      color: $color-black-alpha-80;
    }

    .preset-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .pill {
        @include font-body-small-bold;
        padding: 6px 14px;
        border-radius: 16px;
        border: 1px solid $color-blue-alpha-40;
        background-color: $color-white-solid-10;
        color: $color-blue-solid-100;
        cursor: pointer;
        transition: all $default-transition;

        &:hover {
          background-color: $color-blue-alpha-10;
        }

        &.active {
          background-color: $color-blue-solid-100;
          border-color: $color-blue-solid-100;
          color: $color-white-solid-10;
        }
      }
    }

    .control-group {
      display: flex;
      flex-direction: column;
      gap: 4px;

      label {
        @include font-body-small-bold;
        color: $color-black-alpha-60;
      }

      input, select {
        height: 36px;
        padding: 0 12px;
        border: 1px solid $color-black-alpha-20;
        border-radius: $default-border-radius;
        background-color: $color-white-solid-10;
        @include font-body-medium;
        color: $color-black-solid-100;
        outline: none;

        &:focus {
          border-color: $color-blue-solid-100;
        }
      }

      &.branch-select {
        min-width: 280px;
      }
    }

    .control-actions {
      display: flex;
      align-items: flex-end;
      padding-top: 20px;
    }
  }

  .loading-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    gap: 16px;
    @include font-body-medium;
    color: $color-black-alpha-60;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 32px;

    .kpi-card {
      background-color: $color-white-solid-10;
      border: 1px solid $color-black-alpha-10;
      border-radius: $default-border-radius;
      padding: 20px 24px;
      box-shadow: $box-shadow-1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      &.primary {
        border-left: 4px solid $color-blue-solid-100;
      }

      .kpi-label {
        @include font-body-small-bold;
        color: $color-black-alpha-60;
        text-transform: uppercase;
      }

      .kpi-value {
        @include font-heading1;
        color: $color-blue-solid-100;
        margin: 8px 0 4px;

        &.period {
          @include font-heading2;
          margin: 12px 0 8px;
        }
      }

      .kpi-sub {
        @include font-body-small;
        color: $color-black-alpha-60;
      }
    }
  }

  .overview-panels {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 32px;

    .panel-card {
      background-color: $color-white-solid-10;
      border: 1px solid $color-black-alpha-10;
      border-radius: $default-border-radius;
      padding: 24px;
      box-shadow: $box-shadow-1;

      &.collapsible {
        summary.panel-header {
          cursor: pointer;
          list-style: none;
          display: flex;
          justify-content: space-between;
          align-items: center;

          &::-webkit-details-marker {
            display: none;
          }

          &:after {
            content: 'Ausklappen ▾';
            @include font-body-small-bold;
            color: $color-blue-solid-100;
          }
        }

        &[open] summary.panel-header:after {
          content: 'Einklappen ▴';
        }

        .branch-bars {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid $color-black-alpha-05;
        }
      }

      .panel-header {
        margin-bottom: 16px;

        h2 {
          @include font-body-large-bold;
          margin: 0 0 4px;
        }

        .panel-sub {
          @include font-body-small;
          color: $color-black-alpha-60;
          margin: 0;
        }
      }
    }
  }

  .section-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 1px solid $color-black-alpha-10;

    .sec-tab {
      @include font-body-small-bold;
      padding: 8px 14px;
      border-radius: $default-border-radius;
      border: 1px solid $color-black-alpha-10;
      background-color: $color-white-solid-10;
      color: $color-black-alpha-80;
      cursor: pointer;
      transition: all $default-transition;

      &:hover {
        border-color: $color-blue-solid-80;
        color: $color-blue-solid-100;
      }

      &.active {
        background-color: $color-blue-solid-100;
        border-color: $color-blue-solid-100;
        color: $color-white-solid-10;
      }
    }
  }

  .stat-section {
    margin-bottom: 40px;

    .section-banner {
      background-color: $color-blue-solid-100;
      color: $color-white-solid-10;
      padding: 12px 20px;
      border-radius: $default-border-radius;
      margin-bottom: 20px;

      h2 {
        @include font-body-large-bold;
        color: $color-white-solid-10;
        margin: 0;
      }
    }

    .fields-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(460px, 1fr));
      gap: 20px;

      @include screen-tablet {
        grid-template-columns: 1fr;
      }
    }

    .field-card {
      background-color: $color-white-solid-10;
      border: 1px solid $color-black-alpha-10;
      border-radius: $default-border-radius;
      padding: 20px 24px;
      box-shadow: $box-shadow-1;

      &.full-width {
        grid-column: 1 / -1;
      }

      .field-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid $color-black-alpha-05;

        h3 {
          @include font-body-medium-bold;
          color: $color-black-solid-100;
          margin: 0 0 4px;
        }

        .field-badge {
          display: inline-block;
          @include font-body-tiny-semibold;
          background-color: $color-blue-alpha-10;
          color: $color-blue-solid-100;
          padding: 2px 8px;
          border-radius: 10px;
        }

        .field-n {
          text-align: right;
          white-space: nowrap;
          @include font-body-small;

          .answered {
            font-weight: 700;
            color: $color-blue-solid-100;
          }

          .missing {
            display: block;
            font-size: 1.1rem;
            color: $color-black-alpha-40;
          }
        }
      }
    }
  }

  .bar-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.compact {
      max-height: 320px;
      overflow-y: auto;
      padding-right: 8px;
    }
  }

  .bar-row {
    display: grid;
    grid-template-columns: minmax(180px, 2fr) 3fr 110px;
    align-items: center;
    gap: 12px;
    @include font-body-small;

    &.zero {
      opacity: 0.45;
    }

    .bar-label {
      color: $color-black-solid-100;
      word-break: break-word;

      .meta {
        color: $color-black-alpha-60;
        margin-left: 4px;
      }
    }

    .bar-track {
      width: 100%;
      height: 12px;
      background-color: $color-black-alpha-05;
      border-radius: 6px;
      overflow: hidden;

      .bar-fill {
        height: 100%;
        background-color: $color-blue-solid-100;
        border-radius: 6px;
        transition: width 0.3s ease;
      }
    }

    .bar-stats {
      text-align: right;
      white-space: nowrap;

      .count {
        @include font-body-small-bold;
        color: $color-black-solid-100;
        margin-right: 4px;
      }

      .pct {
        color: $color-black-alpha-60;
      }
    }
  }

  .table-scroll {
    width: 100%;
    overflow-x: auto;
  }

  .stat-table {
    width: 100%;
    border-collapse: collapse;
    @include font-body-small;

    th, td {
      padding: 10px 12px;
      text-align: left;
      border-bottom: 1px solid $color-black-alpha-05;
    }

    th {
      @include font-table-heading;
      text-transform: uppercase;
      color: $color-black-alpha-60;
      background-color: $color-black-solid-05;
    }

    &.interactive tbody tr {
      cursor: pointer;
      transition: background-color $default-transition;

      &:hover {
        background-color: $color-blue-alpha-10;
      }

      &.selected {
        background-color: $color-blue-alpha-20;
      }
    }

    .num {
      text-align: right;
      white-space: nowrap;
    }

    .bold {
      font-weight: 700;
    }

    .muted {
      color: $color-black-alpha-60;
    }

    .code-cell {
      font-family: monospace;
      font-weight: 700;
      color: $color-blue-solid-100;
      white-space: nowrap;
    }

    .sub-pct {
      color: $color-black-alpha-60;
      margin-left: 4px;
      font-size: 1.1rem;
    }

    .bar-col {
      width: 160px;
    }

    .mini-bar-wrap {
      width: 100%;
      height: 10px;
      background-color: $color-black-alpha-05;
      border-radius: 5px;
      overflow: hidden;

      .mini-bar {
        height: 100%;
        background-color: $color-blue-solid-100;
        border-radius: 5px;
      }
    }

    .stack-col {
      width: 150px;
    }

    .stacked-bar {
      display: flex;
      width: 100%;
      height: 12px;
      border-radius: 6px;
      overflow: hidden;
      background-color: $color-black-alpha-05;

      .seg {
        height: 100%;
        &.seg-1 { background-color: $color-signal-green; }
        &.seg-2 { background-color: $color-signal-orange; }
        &.seg-3 { background-color: $color-signal-red; }
        &.seg-4 { background-color: $color-black-alpha-20; }
      }
    }
  }

  .empty-hint {
    @include font-body-small;
    color: $color-black-alpha-60;
    padding: 12px 0;
  }
}
</style>
